import axios from 'axios';

import { ApiResponse } from '../../models/api.response.model';
import { authToken } from '../../scripts/authentication.script';

export class EmployeeDA {
  static apiResponse: ApiResponse

  public static async getEmployees(
    id_company: string,
    search: string,
    status: string,
    page: number,
    limit: number,
    signal?: AbortSignal
  ): Promise<ApiResponse> {
    let params = new URLSearchParams()
    if (search) params.set('search', search)
    if (status) params.set('status', status)
    params.set('page', String(page))
    params.set('limit', String(limit))

    let url = `${process.env.REACT_APP_PORT_ADMIN}/${id_company}/payroll/employees?${params.toString()}`

    let config: any = {
      headers: { Authorization: authToken() },
      signal
    }

    await axios.get(url, config).then(response => {
      this.apiResponse = {
        status: response.status,
        message: response.statusText,
        data: response.data?.data ?? response.data
      }
    }).catch(error => {
      if (axios.isCancel(error)) {
        this.apiResponse = { status: 0, message: 'cancelled', data: null }
        return
      }
      this.apiResponse = {
        status: error.response?.status ?? 500,
        message: error.response?.statusText ?? 'Error',
        data: error.response?.data?.error ?? { code: 'NETWORK_ERROR', message: error.message }
      }
    })

    return this.apiResponse
  }

  public static async getEmployee(
    id_company: string,
    id_employee: string
  ): Promise<ApiResponse> {
    let url = `${process.env.REACT_APP_PORT_ADMIN}/${id_company}/payroll/employees/${id_employee}`

    let config: any = {
      headers: { Authorization: authToken() }
    }

    await axios.get(url, config).then(response => {
      this.apiResponse = {
        status: response.status,
        message: response.statusText,
        data: response.data?.data ?? response.data
      }
    }).catch(error => {
      this.apiResponse = {
        status: error.response?.status ?? 500,
        message: error.response?.statusText ?? 'Error',
        data: error.response?.data?.error ?? { code: 'NETWORK_ERROR', message: error.message }
      }
    })

    return this.apiResponse
  }

  public static async postEmployee(
    id_company: string,
    body: any
  ): Promise<ApiResponse> {
    let url = `${process.env.REACT_APP_PORT_ADMIN}/${id_company}/payroll/employees`

    let config: any = {
      headers: { Authorization: authToken() }
    }

    await axios.post(url, body, config).then(response => {
      this.apiResponse = {
        status: response.status,
        message: response.statusText,
        data: response.data?.data ?? response.data
      }
    }).catch(error => {
      this.apiResponse = {
        status: error.response?.status ?? 500,
        message: error.response?.statusText ?? 'Error',
        data: error.response?.data?.error ?? { code: 'NETWORK_ERROR', message: error.message }
      }
    })

    return this.apiResponse
  }

  public static async putEmployee(
    id_company: string,
    id_employee: string,
    body: any
  ): Promise<ApiResponse> {
    let url = `${process.env.REACT_APP_PORT_ADMIN}/${id_company}/payroll/employees/${id_employee}`

    let config: any = {
      headers: { Authorization: authToken() }
    }

    await axios.put(url, body, config).then(response => {
      this.apiResponse = {
        status: response.status,
        message: response.statusText,
        data: response.data?.data ?? response.data
      }
    }).catch(error => {
      this.apiResponse = {
        status: error.response?.status ?? 500,
        message: error.response?.statusText ?? 'Error',
        data: error.response?.data?.error ?? { code: 'NETWORK_ERROR', message: error.message }
      }
    })

    return this.apiResponse
  }

  public static async uploadVoucher(
    id_company: string,
    id_employee: string,
    file: File,
    role?: string
  ): Promise<ApiResponse> {
    let url = `${process.env.REACT_APP_PORT_ADMIN}/${id_company}/payroll/employees/${id_employee}/vouchers/upload`

    let formData = new FormData()
    formData.append('file', file)
    formData.append('role', role || 'other')

    let config: any = {
      headers: { Authorization: authToken(), 'Content-Type': 'multipart/form-data' }
    }

    await axios.post(url, formData, config).then(response => {
      this.apiResponse = {
        status: response.status,
        message: response.statusText,
        data: response.data?.data ?? response.data
      }
    }).catch(error => {
      this.apiResponse = {
        status: error.response?.status ?? 500,
        message: error.response?.statusText ?? 'Error',
        data: error.response?.data?.error ?? { code: 'NETWORK_ERROR', message: error.message }
      }
    })

    return this.apiResponse
  }

  public static async attachVoucher(
    id_company: string,
    id_employee: string,
    body: { id_document_inbox: string; role?: string }
  ): Promise<ApiResponse> {
    let url = `${process.env.REACT_APP_PORT_ADMIN}/${id_company}/payroll/employees/${id_employee}/vouchers`

    let config: any = {
      headers: { Authorization: authToken() }
    }

    await axios.post(url, body, config).then(response => {
      this.apiResponse = {
        status: response.status,
        message: response.statusText,
        data: response.data?.data ?? response.data
      }
    }).catch(error => {
      this.apiResponse = {
        status: error.response?.status ?? 500,
        message: error.response?.statusText ?? 'Error',
        data: error.response?.data?.error ?? { code: 'NETWORK_ERROR', message: error.message }
      }
    })

    return this.apiResponse
  }

  public static async deleteVoucher(
    id_company: string,
    id_employee: string,
    id_document: string
  ): Promise<ApiResponse> {
    let url = `${process.env.REACT_APP_PORT_ADMIN}/${id_company}/payroll/employees/${id_employee}/vouchers/${id_document}`

    let config: any = {
      headers: { Authorization: authToken() }
    }

    await axios.delete(url, config).then(response => {
      this.apiResponse = {
        status: response.status,
        message: response.statusText,
        data: response.data?.data ?? response.data
      }
    }).catch(error => {
      this.apiResponse = {
        status: error.response?.status ?? 500,
        message: error.response?.statusText ?? 'Error',
        data: error.response?.data?.error ?? { code: 'NETWORK_ERROR', message: error.message }
      }
    })

    return this.apiResponse
  }

  public static async patchExpenseAccess(
    id_company: string,
    id_employee: string,
    expenseAccess: string
  ): Promise<ApiResponse> {
    let url = `${process.env.REACT_APP_PORT_ADMIN}/${id_company}/payroll/employees/${id_employee}/expense-access`

    let config: any = {
      headers: { Authorization: authToken() }
    }

    await axios.patch(url, { expenseAccess }, config).then(response => {
      this.apiResponse = {
        status: response.status,
        message: response.statusText,
        data: response.data?.data ?? response.data
      }
    }).catch(error => {
      this.apiResponse = {
        status: error.response?.status ?? 500,
        message: error.response?.statusText ?? 'Error',
        data: error.response?.data?.error ?? { code: 'NETWORK_ERROR', message: error.message }
      }
    })

    return this.apiResponse
  }

  public static async deleteEmployee(
    id_company: string,
    id_employee: string
  ): Promise<ApiResponse> {
    let url = `${process.env.REACT_APP_PORT_ADMIN}/${id_company}/payroll/employees/${id_employee}`

    let config: any = {
      headers: { Authorization: authToken() }
    }

    await axios.delete(url, config).then(response => {
      this.apiResponse = {
        status: response.status,
        message: response.statusText,
        data: response.data?.data ?? response.data
      }
    }).catch(error => {
      this.apiResponse = {
        status: error.response?.status ?? 500,
        message: error.response?.statusText ?? 'Error',
        data: error.response?.data?.error ?? { code: 'NETWORK_ERROR', message: error.message }
      }
    })

    return this.apiResponse
  }
}
